import { X, Trash2, Plus, Minus, ShoppingBag, ArrowRight, Eye } from 'lucide-react';
import { useCartActions, useCartState } from '../../context/CartContext';
import { useTheme } from '../../context/ThemeContext';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { r2Url } from "@/lib/r2";

const PLACEHOLDER_IMG = 'https://placehold.co/800x1000/png?text=No+Image';
const formatMNT = (value: number): string =>
  `₮${Math.max(0, Math.round(value)).toLocaleString()}`;

export default function CartSidebar() {
  const { cart, isCartOpen, cartTotal } = useCartState();
  const { setIsCartOpen, increaseQty, decreaseQty, removeItem } = useCartActions();
  const { language } = useTheme();

  const groupedCart = useMemo(() => {
    const products = new Map<
      string,
      {
        productId: string;
        productName: string;
        productCategory: string;
        variants: Map<string, Map<string, typeof cart[number]>>;
      }
    >();

    for (const item of cart) {
      const productId = item.productId;
      const variantId = item.variantId;
      const sizeKey = item.size ?? 'none';

      if (!products.has(productId)) {
        products.set(productId, {
          productId: item.productId,
          productName: item.productName,
          productCategory: item.productCategory,
          variants: new Map(),
        });
      }

      const entry = products.get(productId)!;
      if (!entry.variants.has(variantId)) {
        entry.variants.set(variantId, new Map());
      }
      entry.variants.get(variantId)!.set(sizeKey, item);
    }

    return Array.from(products.values());
  }, [cart]);

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="flex h-full w-full flex-col border-l border-[#e6e6e6] bg-[#f5f5f5] shadow-2xl sm:max-w-md"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#e6e6e6] bg-white px-6 py-5">
          <SheetTitle className="flex items-center gap-2 text-xl font-black text-[#111318]">
            <ShoppingBag className="text-[#f6a800]" size={22} />
            {language === 'mn' ? 'Таны Сагс' : 'Your Cart'}
            <span className="rounded-full bg-[#f7f7f7] px-2 py-0.5 text-sm font-black text-[#747982]">
              {cart.length}
            </span>
          </SheetTitle>
          <SheetDescription className="sr-only">
            {language === 'mn'
              ? `Таны сагсанд ${cart.length} бараа байна. Дэлгэрэнгүйг доор үзнэ үү.`
              : `Your shopping cart contains ${cart.length} items. View details below.`}
          </SheetDescription>
          <button
            onClick={() => setIsCartOpen(false)}
            className="group rounded-full p-2 transition-all hover:bg-[#f7f7f7] active:scale-90"
          >
            <X size={20} className="text-[#747982] transition-colors group-hover:text-[#111318]" />
          </button>
        </div>

        {/* Cart Items List */}
        <div className="flex-1 space-y-4 overflow-y-auto p-5">
          {cart.length === 0 ? (
            <div data-testid="cart-empty" className="flex h-full flex-col items-center justify-center space-y-6 text-center">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-[#f6a800]/20 blur-xl"></div>
                <div className="relative flex h-20 w-20 items-center justify-center rounded-[22px] bg-white text-[#f6a800] shadow-sm ring-1 ring-[#e6e6e6]">
                  <ShoppingBag size={40} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-black text-[#111318]">
                  {language === 'mn' ? 'Сагс хоосон байна' : 'Your cart is empty'}
                </h3>
                <button
                  onClick={() => setIsCartOpen(false)}
                  className="mt-6 rounded-[12px] bg-[#f6a800] px-8 py-3 font-black text-[#111318] transition-all hover:bg-[#e99f00]"
                >
                  {language === 'mn' ? 'Дэлгүүр хэсэх' : 'Start Shopping'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {groupedCart.map(({ productId, productName, productCategory, variants }) => {
                return (
                  <div
                    key={productId}
                    data-testid="cart-item"
                    className="group relative rounded-[18px] border border-[#e6e6e6] bg-white p-4 shadow-sm transition-all hover:border-[#d8d8d8] hover:shadow-md"
                  >
                    <div className="flex flex-col gap-3">
                      {/* Product Header */}
                      <div>
                        <h3 className="text-base font-black text-[#25282d]">
                          {productName}
                        </h3>
                        <p className="text-xs font-bold uppercase tracking-wide text-[#747982]">
                          {productCategory}
                        </p>
                      </div>

                      {/* Variants */}
                      <div className="space-y-3">
                        {Array.from(variants.entries()).map(([variantId, sizeMap]) => {
                          const firstItem = Array.from(sizeMap.values())[0];
                          const imgSrc = r2Url(firstItem.variantImage) || PLACEHOLDER_IMG;
                          const variantTotal = Array.from(sizeMap.values())
                            .reduce((acc, i) => acc + i.variantPrice * i.quantity, 0);

                          return (
                            <div key={variantId} className="flex gap-3 border-t border-[#eeeeee] pt-3 first:border-t-0 first:pt-0">
                              {/* Variant Image */}
                              <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-[14px] border border-[#eeeeee] bg-[#f7f7f7]">
                                <img
                                  src={imgSrc}
                                  alt={firstItem.variantName}
                                  className="h-full w-full object-contain object-center p-1 transition-transform duration-500 group-hover:scale-105"
                                />
                              </div>

                              {/* Variant Info */}
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-extrabold text-[#25282d]">
                                    {firstItem.variantName}
                                  </span>
                                  <span data-testid="item-price" className="text-sm font-black text-[#f6a800]">
                                    {formatMNT(variantTotal)}
                                  </span>
                                </div>

                                {/* Sizes */}
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {Array.from(sizeMap.entries()).map(([sizeKey, item]) => (
                                    <div
                                      key={item.cartKey}
                                      className="flex items-center gap-1 rounded-[10px] bg-[#f7f7f7] px-2 py-1"
                                    >
                                      <span data-testid="item-quantity" className="text-[11px] font-black text-[#111318]">
                                        {sizeKey === 'none' ? 'No size' : sizeKey} ({item.quantity})
                                      </span>
                                      <button data-testid="decrease-quantity" onClick={() => decreaseQty(item.cartKey)} className="ml-1 hover:text-[#b87900]">
                                        <Minus size={12} />
                                      </button>
                                      <button data-testid="increase-quantity" onClick={() => increaseQty(item.cartKey)} className="hover:text-[#b87900]">
                                        <Plus size={12} />
                                      </button>
                                      <button data-testid="remove-item" onClick={() => removeItem(item.cartKey)} className="ml-1 text-[#8a8f98] hover:text-[#ef4444]">
                                        <Trash2 size={12} />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div className="space-y-3 border-t border-[#e6e6e6] bg-white p-6">
            <div className="flex justify-between text-lg font-black text-[#111318]">
              <p>{language === 'mn' ? 'Нийт дүн' : 'Total'}</p>
              <p data-testid="cart-total" className="text-[#f6a800]">{formatMNT(cartTotal)}</p>
            </div>
            <Link
              to="/cart"
              onClick={() => setIsCartOpen(false)}
              className="flex w-full items-center justify-center gap-2 rounded-[12px] border border-[#e6e6e6] px-6 py-3 font-black text-[#111318] transition-colors hover:border-[#f6a800] hover:bg-[#fff8ea]"
            >
              <Eye size={18} />
              {language === 'mn' ? 'Сагс үзэх' : 'VIEW CART'}
            </Link>
            <Link
              to="/checkout"
              data-testid="checkout-btn"
              onClick={() => setIsCartOpen(false)}
              className="flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#f6a800] px-6 py-3 font-black text-[#111318] transition-colors hover:bg-[#e99f00]"
            >
              {language === 'mn' ? 'Худалдан авах' : 'CHECKOUT'}
              <ArrowRight size={18} />
            </Link>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
